/**
 * Live chat: one room per stream (see apps/api/src/stream-room.ts). Messages are sent over the API
 * and pushed to viewers over the stream's WebSocket, next to status and playlists.
 */

import { blockedTextMessage, blockedTextReason } from './text-filter.ts';

export const CHAT_MAX_CHARS = 200;
/** Messages a viewer gets on connect, and per page of history. */
export const CHAT_TAIL = 50;
export const CHAT_SLOW_MODES = [0, 5, 30] as const;
export const CHAT_TIMEOUT_SECONDS = 5 * 60;

/** host: the stream's creator; mod: someone the creator trusts with the chat tools (always has an account). */
export type ChatBadge = 'host' | 'mod' | 'verified' | null;

export interface ChatMessage {
  /** Order in the room, assigned by the server. Also the message's id. */
  seq: number;
  /** Epoch ms. */
  at: number;
  /** `key` is an opaque per-person id (never the account id): stable colour, hide, moderation. */
  author: { key: string; name: string; badge: ChatBadge };
  text: string;
  reply: { seq: number; name: string; text: string } | null;
}

export type ChatMode = 'everyone' | 'verified' | 'off';

export interface ChatSettings {
  mode: ChatMode;
  slowSeconds: number;
  pinned: ChatMessage | null;
}

export const DEFAULT_CHAT_SETTINGS: ChatSettings = { mode: 'everyone', slowSeconds: 0, pinned: null };

/** Pushed over the stream WebSocket. Old clients ignore types they don't know. */
export type ChatEvent =
  | { type: 'chat'; messages: ChatMessage[] }
  | { type: 'chat.removed'; seqs: number[] }
  | { type: 'chat.settings'; settings: ChatSettings }
  /** On connect: the latest messages and the room's settings. */
  | { type: 'chat.tail'; messages: ChatMessage[]; settings: ChatSettings };

export interface ChatSnapshot {
  messages: ChatMessage[];
  settings: ChatSettings;
}

/** Messages by seq, oldest first, without duplicates; only the newest `keep` are kept. */
export function mergeChatMessages(a: ChatMessage[], b: ChatMessage[], keep = Infinity): ChatMessage[] {
  const bySeq = new Map<number, ChatMessage>();
  for (const m of a) bySeq.set(m.seq, m);
  for (const m of b) bySeq.set(m.seq, m);
  const all = [...bySeq.values()].sort((x, y) => x.seq - y.seq);
  return all.length > keep ? all.slice(all.length - keep) : all;
}

/** Apply a pushed event to the recent messages a room (or a page) holds. */
export function applyChatEvent(snap: ChatSnapshot, ev: ChatEvent, keep = CHAT_TAIL): ChatSnapshot {
  switch (ev.type) {
    case 'chat':
      return { ...snap, messages: mergeChatMessages(snap.messages, ev.messages, keep) };
    case 'chat.removed': {
      const gone = new Set(ev.seqs);
      const pinned = snap.settings.pinned && gone.has(snap.settings.pinned.seq) ? null : snap.settings.pinned;
      return { messages: snap.messages.filter((m) => !gone.has(m.seq)), settings: { ...snap.settings, pinned } };
    }
    case 'chat.settings':
      return { ...snap, settings: ev.settings };
    case 'chat.tail':
      return { messages: mergeChatMessages([], ev.messages, keep), settings: ev.settings };
  }
}

export const CHAT_REPORT_REASONS = ['spam', 'harassment', 'hate', 'sexual', 'violence', 'other'] as const;

// Invisible and direction-changing characters: used to spoof names and hide text.
const INVISIBLE = /[\u0000-\u001f\u007f-\u009f­͏؜ᅟᅠ឴឵᠋-᠏​-‏‪-‮⁠-⁯ㅤ︀-️﻿ﾠ]/g;

/** One line, no invisible characters, single spaces. Emoji joiners (U+200D) are kept. */
export function normalizeChatText(raw: string): string {
  return raw
    .normalize('NFC')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(INVISIBLE, (ch) => (ch === '‍' ? ch : ''))
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Length as people count it (emoji and accented letters count once). */
export function chatLength(text: string): number {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    let n = 0;
    for (const _ of new Intl.Segmenter().segment(text)) n++;
    return n;
  }
  return [...text].length;
}

const LINK = /\b(?:https?:\/\/|www\.)\S+|\b[a-z0-9-]{2,63}\.(?:com|net|org|io|co|gg|tv|me|ly|xyz|link|app|live|info|biz|ru|cn|top|site|online|shop|club)\b(?:\/\S*)?/i;

export function containsLink(text: string): boolean {
  return LINK.test(text);
}

const RESERVED_NAMES = new Set(['zip2live', 'ziptolive', 'host', 'admin', 'administrator', 'moderator', 'mod', 'staff', 'support', 'system', 'official', 'team']);

/** Lowercase letters and digits only: how two names are compared for lookalikes. */
export function nameKey(name: string): string {
  return name.normalize('NFKC').toLowerCase().replace(/^@/, '').replace(/[^\p{L}\p{N}]/gu, '');
}

export type ChatNameCheck = { ok: true; name: string } | { ok: false; reason: string };

/** A chat name a guest or signed-in viewer picks. Handles (@name) are shown for verified accounts instead. */
export function validateChatName(raw: string): ChatNameCheck {
  const name = normalizeChatText(raw);
  if (name.startsWith('@')) return { ok: false, reason: 'Names can’t start with @.' };
  const len = chatLength(name);
  if (len < 2) return { ok: false, reason: 'Use at least 2 characters.' };
  if (len > 24) return { ok: false, reason: 'Use 24 characters or fewer.' };
  if (!/^[\p{L}\p{N}][\p{L}\p{N}\p{M} ._'-]*$/u.test(name)) return { ok: false, reason: 'Use letters, numbers, spaces, dots, dashes or underscores.' };
  if (RESERVED_NAMES.has(nameKey(name))) return { ok: false, reason: 'That name is reserved. Pick another.' };
  const blocked = blockedTextReason(name);
  if (blocked) return { ok: false, reason: blockedTextMessage(blocked, 'That name') };
  return { ok: true, name };
}
